import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Checkbox, Select, Spin, Tag } from 'antd';
import {
  useFilterPagination,
  type PaginatedFetcher,
} from '@/pages/Shipments/Filters/hooks/useFilterPagination';
import SearchInput from '@/components/SearchInput';
import styles from '../NodeCustomization.module.css';
import type { ListItem } from '@/pages/Shipments/ControlTower/types';

const { Option } = Select;

export interface SelectedLocation {
  id: string;
  name: string;
}

interface LocationFilterProps {
  label: string;
  fetcher: PaginatedFetcher<ListItem>;
  selectedLocations: SelectedLocation[];
  onChangeLocations: (locations: SelectedLocation[]) => void;
  isReadOnly?: boolean;
}

/**
 * A searchable, paginated location picker rendered as an Ant Design Select dropdown.
 * A SearchInput is rendered inside the dropdown via popupRender for server-side search.
 * Loads more results on scroll via useFilterPagination.
 */
const LocationFilter: React.FC<LocationFilterProps> = ({
  label,
  fetcher,
  selectedLocations,
  onChangeLocations,
  isReadOnly,
}) => {
  const selectedSetRef = useRef(new Set<string>());

  useEffect(() => {
    selectedSetRef.current = new Set(selectedLocations.map((l) => l.id));
  }, [selectedLocations]);

  const pg = useFilterPagination<ListItem>({
    enabled: true,
    fetcher,
    limit: 50,
    debounceMs: 500,
  });

  // Build a lookup from id -> name so we can resolve names for selected IDs
  // that may not be in the current paginated results.
  const nameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const loc of selectedLocations) {
      map.set(loc.id, loc.name);
    }
    for (const loc of pg.data) {
      const id = String(loc.id);
      const withExtras = loc as ListItem & { display_name?: string };
      map.set(id, loc.name ?? withExtras.display_name ?? loc.title ?? id);
    }
    return map;
  }, [selectedLocations, pg.data]);

  const handleChange = useCallback(
    (values: string[]) => {
      const updated = values.map((id) => ({
        id,
        name: nameById.get(id) ?? id,
      }));
      onChangeLocations(updated);
    },
    [nameById, onChangeLocations]
  );

  const { loadMore, setSearchDebounced } = pg;
  const handlePopupScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const el = e.currentTarget;
      const threshold = 40;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - threshold) {
        loadMore();
      }
    },
    [loadMore]
  );

  const dropdownRender = useCallback(
    (menu: React.ReactElement) => (
      <>
        {/* preventDefault on mousedown keeps the Select dropdown open.
            onClick manually focuses the input since preventDefault blocks native focus. */}
        <div
          style={{ padding: '8px 8px 8px' }}
          onMouseDown={(e) => e.preventDefault()}
          onClick={(e) => {
            const input = e.currentTarget.querySelector('input');
            if (input) input.focus();
          }}
        >
          <SearchInput
            placeholder='Search locations...'
            onChange={(val: string) => setSearchDebounced(val)}
            onClear={() => setSearchDebounced('')}
            style={{ width: '100%' }}
          />
        </div>
        {menu}
      </>
    ),
    [setSearchDebounced]
  );

  return (
    <div className={styles.customizationSection}>
      <label className={styles.sectionLabel}>{label}</label>
      <Select
        mode='multiple'
        placeholder='Select locations...'
        value={selectedLocations.map((l) => l.id)}
        onChange={handleChange}
        onPopupScroll={handlePopupScroll}
        showSearch={false}
        className={styles.filterDropdown}
        style={{
          background: 'var(--light-gray)',
          border: 'none',
          boxShadow: 'none',
        }}
        optionLabelProp='label'
        disabled={isReadOnly}
        notFoundContent={
          pg.loading ? <Spin size='small' /> : 'No locations found'
        }
        loading={pg.loading}
        popupRender={dropdownRender}
        tagRender={({ label: tagLabel, value, closable, onClose }) => {
          const name = nameById.get(String(value)) ?? String(value);
          return (
            <Tag
              className={styles.filterTag}
              closable={closable}
              onClose={onClose}
            >
              {tagLabel || name}
            </Tag>
          );
        }}
      >
        {pg.data.map((loc) => {
          const id = String(loc.id);
          const withExtras = loc as ListItem & {
            address?: string;
            display_name?: string;
          };
          const name = loc.name ?? withExtras.display_name ?? loc.title ?? id;
          const address = withExtras.address;
          return (
            <Option key={id} value={id} label={name}>
              <Checkbox
                className={styles.filterCheckbox}
                checked={selectedSetRef.current.has(id)}
              >
                <div className={styles.locationOptionName}>{name}</div>
                {address && (
                  <div className={styles.locationOptionAddress}>{address}</div>
                )}
              </Checkbox>
            </Option>
          );
        })}
      </Select>
    </div>
  );
};

export default LocationFilter;
